export type PdfAnalysisMode = "TEXT" | "IMAGE";

export interface PdfVisionPageDescription {
  pageNumber: number;
  rawDescription: string;
  summary: string;
  structuredInsights?: {
    bulletPoints?: string[];
    rolesAndResponsibilities?: string[];
    flowsOrProcesses?: string[];
  };
}

export interface PdfVisionIntermediateResult {
  pages: PdfVisionPageDescription[];
  overallVisualSummary: string;
}

export interface PdfAnalysisResult {
  mode: PdfAnalysisMode;
  summary: string;
  keyPoints: string[];
  risks: string[];
  nextSteps: string[];
  visionDetails?: PdfVisionIntermediateResult;
  modelInfo?: string;
}

interface ApiResponse {
  ok?: boolean;
  error?: string;
  reason?: string;
  analysis?: PdfAnalysisResult;
  message?: string;
}

function normalizeResult(payload: ApiResponse): PdfAnalysisResult {
  const source = (payload.analysis ?? (payload as unknown)) as Partial<PdfAnalysisResult> & {
    risksOrOpenQuestions?: string[];
    suggestedNextSteps?: string[];
  };

  return {
    mode: source.mode ?? "TEXT",
    summary: source.summary ?? "",
    keyPoints: source.keyPoints ?? [],
    risks: source.risks ?? source.risksOrOpenQuestions ?? [],
    nextSteps: source.nextSteps ?? source.suggestedNextSteps ?? [],
    visionDetails: source.visionDetails,
    modelInfo: source.modelInfo,
  };
}

function formatErrorMessage(status: number, payload: ApiResponse | null): string {
  if (status === 404) {
    return "Projet ou document introuvable pour l'analyse.";
  }

  if (status >= 500) {
    return payload?.message ?? "Erreur interne lors de l'analyse du PDF, réessaie plus tard.";
  }

  return payload?.message ?? payload?.error ?? payload?.reason ?? `Analyse du PDF impossible (${status})`;
}

export async function analyzeProjectDocumentPdf(
  projectId: string,
  documentId: string,
  options?: { userContext?: string; mode?: PdfAnalysisMode },
): Promise<PdfAnalysisResult> {
  if (!projectId) {
    throw new Error("Le projet est obligatoire pour analyser le PDF.");
  }

  if (!documentId) {
    throw new Error("Le document est obligatoire pour analyser le PDF.");
  }

  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}/analyze-pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userContext: options?.userContext, mode: options?.mode ?? "TEXT" }),
  });

  let payload: ApiResponse | null = null;
  try {
    payload = (await response.json()) as ApiResponse;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(formatErrorMessage(response.status, payload));
  }

  if (payload?.error && !payload.analysis) {
    throw new Error(payload.message ?? payload.error);
  }

  const result = payload ? normalizeResult(payload) : null;

  if (!result) {
    throw new Error("Réponse d'analyse du PDF invalide.");
  }

  return result;
}

export async function getPdfAnalysis(
  projectId: string,
  documentId: string,
): Promise<PdfAnalysisResult | null> {
  if (!projectId || !documentId) return null;

  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}/pdf-analysis`);

  if (response.status === 404 || response.status === 204) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Impossible de charger l'analyse du PDF (${response.status}).`);
  }

  try {
    const payload = (await response.json()) as ApiResponse;
    return payload.analysis ?? ((payload as unknown) as PdfAnalysisResult);
  } catch (error) {
    throw new Error((error as Error | undefined)?.message ?? "Réponse d'analyse du PDF invalide.");
  }
}
